import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import client, { absoluteUrl } from "../api/client";
import { useAuth } from "../context/AuthContext";
import useImageCatalog from "../hooks/useImageCatalog";
import { capitalise } from "../utils/text";
import ImagePopup from "../Popup/ImagePopup";
import AssetCard from "./AssetCard";
import ArtworkPreview from "./ArtworkPreview";
import "./Wallpaper.css";

/**
 * The shared artwork gallery: paging, filtering, favourites and preview.
 *
 * Every archive page renders through this one component. Images arrive a page
 * at a time and load more on scroll, collections come from the backend's real
 * folder names, and favourites persist per user. An API failure surfaces as an
 * error rather than falling back to placeholder artwork.
 */

const GRID_COLUMN_OPTIONS = [1, 2, 3, 4];
const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "name", label: "Name" },
  { value: "views", label: "Most viewed" },
];

const getColumnCount = () => {
  if (typeof window === "undefined") return 4;
  if (window.matchMedia("(max-width: 720px)").matches) return 1;
  if (window.matchMedia("(max-width: 980px)").matches) return 2;
  if (window.matchMedia("(max-width: 1280px)").matches) return 3;
  return 4;
};

/**
 * Distribute items into masonry columns by real aspect ratio, so columns end
 * up close to equal height.
 */
const EXTENSION_FOR_TYPE = {
  "image/png": ".png",
  "image/jpeg": ".jpg",
  "image/webp": ".webp",
  "image/gif": ".gif",
  "image/tiff": ".tif",
  "image/svg+xml": ".svg",
};

/**
 * Work out the filename to save as.
 *
 * Content-Disposition is only readable when the API exposes it via CORS, and
 * file_name is stored without an extension, so both fallbacks are needed.
 */
const downloadFilename = (asset, headers, contentType) => {
  const disposition = headers["content-disposition"];
  const utf8 = disposition?.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) return decodeURIComponent(utf8[1]);
  const plain = disposition?.match(/filename="?([^";]+)"?/i);
  if (plain) return plain[1].trim();

  const base = asset.file_name || asset.file_id || "artwork";
  if (/\.[a-z0-9]{2,5}$/i.test(base)) return base;
  const ext = EXTENSION_FOR_TYPE[String(contentType).split(";")[0].trim()] || "";
  return `${base}${ext}`;
};

const buildMasonryColumns = (assets, columnCount) => {
  const columns = Array.from({ length: columnCount }, () => ({
    items: [],
    height: 0,
  }));

  assets.forEach((asset) => {
    const shortest = columns.reduce(
      (best, column, index) =>
        column.height < columns[best].height ? index : best,
      0
    );
    columns[shortest].items.push(asset);
    columns[shortest].height += asset.aspect_ratio || 1;
  });

  return columns.map((column) => column.items);
};

function ArchiveGallery({
  category = "all",
  collectionLabel = "Collections",
  emptyTitle = "No artwork found in this section.",
  favoritesOnly = false,
  showCollectionFilter = true,
}) {
  const { isAdmin } = useAuth();

  const [activeCategory, setActiveCategory] = useState(category);
  const [sort, setSort] = useState("newest");
  const [categories, setCategories] = useState([]);
  const [responsiveColumns, setResponsiveColumns] = useState(getColumnCount);
  const [selectedColumns, setSelectedColumns] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [actionError, setActionError] = useState("");

  const columnCount = selectedColumns || responsiveColumns;
  const sentinelRef = useRef(null);

  useEffect(() => setActiveCategory(category), [category]);

  const {
    items,
    total,
    hasMore,
    loading,
    loadingMore,
    error,
    loadMore,
    updateItem,
    removeItem,
  } = useImageCatalog({ category: activeCategory, favoritesOnly, sort });

  // Real collections with live counts, from the data.
  useEffect(() => {
    if (!showCollectionFilter) return;
    client
      .get("/api/images/categories")
      .then(({ data }) => setCategories(data.categories || []))
      .catch((err) => console.error("Could not load categories:", err));
  }, [showCollectionFilter]);

  useEffect(() => {
    const onResize = () => setResponsiveColumns(getColumnCount());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Infinite scroll: load the next page when the sentinel scrolls into view.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "400px 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const masonryColumns = useMemo(
    () => buildMasonryColumns(items, columnCount),
    [items, columnCount]
  );

  const handleView = useCallback(
    async (asset) => {
      setSelectedAsset(asset);
      setPreviewUrl(null);
      setActionError("");

      try {
        // One call returns the fresh signed URL for the full-resolution file
        // and the current counters.
        const { data } = await client.get(`/api/images/${asset.file_id}`);
        setPreviewUrl(absoluteUrl(data.full_url));
        updateItem(asset.file_id, data);
        setSelectedAsset((current) => ({ ...current, ...data }));

        client
          .post(`/api/image/view/${asset.file_id}`)
          .then(({ data: viewData }) => {
            updateItem(asset.file_id, { views: viewData.views });
            // Patch the open panel too, otherwise it keeps showing the count
            // from before this very view was recorded while the card behind
            // it already shows the new one.
            setSelectedAsset((current) =>
              current?.file_id === asset.file_id
                ? { ...current, views: viewData.views }
                : current
            );
          })
          .catch((err) => console.error("Could not record view:", err));
      } catch (err) {
        console.error("Preview failed:", err);
        setActionError("Could not open this artwork.");
      }
    },
    [updateItem]
  );

  const handleToggleFavorite = useCallback(
    async (asset) => {
      const nextState = !asset.is_favorite;
      // Optimistic, reverted below if the request fails.
      updateItem(asset.file_id, { is_favorite: nextState });
      setSelectedAsset((current) =>
        current?.file_id === asset.file_id
          ? { ...current, is_favorite: nextState }
          : current
      );

      try {
        if (nextState) {
          await client.post(`/api/favorites/${asset.file_id}`);
        } else {
          await client.delete(`/api/favorites/${asset.file_id}`);
          if (favoritesOnly) removeItem(asset.file_id);
        }
      } catch (err) {
        console.error("Favourite toggle failed:", err);
        updateItem(asset.file_id, { is_favorite: !nextState });
        setActionError("Could not update your favourites.");
      }
    },
    [updateItem, removeItem, favoritesOnly]
  );

  const handleDownload = useCallback(async (asset) => {
    setActionError("");
    try {
      const response = await client.get(`/api/download/${asset.file_id}`, {
        responseType: "blob",
      });

      const contentType =
        response.headers["content-type"] || "application/octet-stream";
      // Typing the blob matters: an untyped blob with an extension-less name
      // makes Chrome save the file as .txt.
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = downloadFilename(asset, response.headers, contentType);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      setActionError("Could not download that file.");
    }
  }, []);

  const closeModal = () => {
    setSelectedAsset(null);
    setPreviewUrl(null);
  };

  // The "All" count is the whole library, summed from the category counts.
  // Using the list's own `total` would show the *filtered* count, so picking
  // "wood" made the All chip read 6 as well.
  //
  // In the favourites view the counts are suppressed entirely: they describe
  // the whole library, which would sit confusingly beside a list of only the
  // viewer's favourites.
  const libraryTotal = categories.reduce((sum, item) => sum + item.count, 0);

  const collections = showCollectionFilter
    ? [
        { key: "all", label: "All", count: favoritesOnly ? null : libraryTotal },
        ...categories.map((item) =>
          favoritesOnly ? { ...item, count: null } : item
        ),
      ]
    : [];

  return (
    <main className="wallpaper-archive">
      <section className="collection-rail" aria-label={collectionLabel}>
        {showCollectionFilter && (
          <div className="collection-filter-group">
            <div className="collection-rail-label">
              <SlidersHorizontal size={16} aria-hidden="true" />
              {collectionLabel}
            </div>
            <div className="collection-chips">
              {collections.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={item.key === activeCategory ? "active" : ""}
                  onClick={() => setActiveCategory(item.key)}
                >
                  {capitalise(item.label)}
                  {typeof item.count === "number" && (
                    <span className="chip-count">{item.count}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="archive-controls">
          <label className="archive-sort">
            <span className="sr-only">Sort artwork</span>
            <select value={sort} onChange={(event) => setSort(event.target.value)}>
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <div className="grid-density-controls" aria-label="Grid density">
            {GRID_COLUMN_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={option === columnCount ? "active" : ""}
                onClick={() => setSelectedColumns(option)}
                title={`${option} column grid`}
                aria-label={`Show ${option} column grid`}
                aria-pressed={option === columnCount}
              >
                <span
                  className="grid-density-icon"
                  style={{ "--grid-density-count": option }}
                  aria-hidden="true"
                >
                  {Array.from({ length: option }).map((_, index) => (
                    <span key={index} />
                  ))}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {(error || actionError) && (
        <p className="archive-status" role="alert">
          {error || actionError}
        </p>
      )}

      {!loading && items.length > 0 && (
        <p className="archive-count">
          Showing {items.length} of {total}
        </p>
      )}

      {loading ? (
        <section
          className="asset-masonry"
          style={{ "--masonry-columns": columnCount }}
          aria-label="Loading artwork archive"
        >
          {Array.from({ length: columnCount }).map((_, columnIndex) => (
            <div className="asset-masonry-column" key={columnIndex}>
              {Array.from({ length: 3 }).map((__, index) => (
                <div className="asset-skeleton" key={index} />
              ))}
            </div>
          ))}
        </section>
      ) : items.length ? (
        <>
          <section
            className="asset-masonry"
            style={{ "--masonry-columns": columnCount }}
            aria-label="Artwork archive grid"
          >
            {masonryColumns.map((column, columnIndex) => (
              <div className="asset-masonry-column" key={columnIndex}>
                {column.map((asset) => (
                  <AssetCard
                    key={asset.file_id}
                    asset={asset}
                    imageUrl={absoluteUrl(asset.thumbnail_path)}
                    showDownloadCount={isAdmin}
                    onView={() => handleView(asset)}
                    onDownload={() => handleDownload(asset)}
                    onToggleFavorite={() => handleToggleFavorite(asset)}
                  />
                ))}
              </div>
            ))}
          </section>

          <div ref={sentinelRef} className="archive-sentinel" aria-hidden="true" />

          {loadingMore && <p className="archive-status">Loading more...</p>}
          {!hasMore && items.length > 0 && (
            <p className="archive-status archive-end">End of archive</p>
          )}
        </>
      ) : (
        <section className="archive-empty">
          <p className="asset-ticket-label">NOTHING HERE</p>
          <h2>{emptyTitle}</h2>
        </section>
      )}

      <ImagePopup isOpen={selectedAsset !== null} closeModal={closeModal}>
        <ArtworkPreview
          asset={selectedAsset}
          imageUrl={previewUrl}
          showDownloadCount={isAdmin}
          onToggleFavorite={() =>
            selectedAsset && handleToggleFavorite(selectedAsset)
          }
          onDownload={() => selectedAsset && handleDownload(selectedAsset)}
        />
      </ImagePopup>
    </main>
  );
}

export default ArchiveGallery;
